import React from 'react';
import ReactDOM from 'react-dom/client';
import './index.css';
import App from './App';
import reportWebVitals from './reportWebVitals';
import posthog from 'posthog-js';
import { HelmetProvider } from 'react-helmet-async';

posthog.init(process.env.REACT_APP_POSTHOG_KEY, {
    api_host: '/nova-beacon',
    ui_host: 'https://eu.posthog.com',

    // Autocapture is ON by default (we don't disable it) — every click,
    // including the quiz "Next" button, is already recorded. Named marker
    // events below just make the key moments filterable/jumpable in replay.

    capture_pageview: true,
    capture_pageleave: true,

    // --- Session replay tuned for debugging forensics ---
    session_recording: {
        // Inputs left visible so a replay shows exactly what the child typed /
        // selected — essential for "real bug vs. child did something silly".
        // NOTE (children's data): this records their answers verbatim. See the
        // privacy trade-off note in the PR description before shipping.
        maskAllInputs: false,
        maskInputOptions: { password: true },

        // Capture network requests onto the replay timeline (payloads + headers)
        // so you can see the actual API call/response at the moment of a click —
        // e.g. quiz submit returned 200 but UI didn't advance = real bug.
        // Requires "Capture network requests" enabled in PostHog project
        // settings too (client flag alone is not enough).
        recordHeaders: true,
        recordBody: true,
        maskNetworkRequestFn: (request) => {
            // Never persist auth/session secrets into recordings.
            const stripped = ['authorization', 'Authorization', 'cookie', 'Cookie'];
            if (request && request.requestHeaders) {
                stripped.forEach((h) => delete request.requestHeaders[h]);
            }
            if (request && request.responseHeaders) {
                stripped.forEach((h) => delete request.responseHeaders[h]);
            }
            // Redact bodies of endpoints that return bulk child PII you don't
            // need for debugging (uncomment / extend as needed):
            // if (request?.name?.includes('/children/')) {
            //     request.responseBody = '[redacted]';
            // }
            return request;
        },
    },

    // Surface console.log/console.error (there are many across the call flow)
    // onto the replay timeline.
    enable_recording_console_log: true,

    // Network timing waterfall in replay.
    capture_performance: true,

    // Auto-flag any session where a JS exception fired (Error Tracking).
    capture_exceptions: true,
});

// Robust global error capture — replaces the old commented-out
// setupGlobalErrorLogging. Works regardless of the built-in autocapture flag,
// so uncaught errors and rejected promises always tag their replay session.
if (typeof window !== 'undefined') {
    window.addEventListener('error', (event) => {
        if (typeof posthog.captureException === 'function') {
            posthog.captureException(event.error ?? new Error(event.message), {
                source: 'window.onerror',
            });
        }
    });
    window.addEventListener('unhandledrejection', (event) => {
        if (typeof posthog.captureException === 'function') {
            const reason = event.reason instanceof Error
                ? event.reason
                : new Error(typeof event.reason === 'string' ? event.reason : 'unhandledrejection');
            posthog.captureException(reason, { source: 'unhandledrejection' });
        }
    });
}
const root = ReactDOM.createRoot(document.getElementById('root'));
root.render(
  <React.StrictMode>
    <HelmetProvider>
      <App />
    </HelmetProvider>
  </React.StrictMode>
);

// If you want to start measuring performance in your app, pass a function
// to log results (for example: reportWebVitals(console.log))
// or send to an analytics endpoint. Learn more: https://bit.ly/CRA-vitals
reportWebVitals();
